import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Target, 
  CheckCircle2, 
  Circle, 
  ChevronUp, 
  ChevronDown,
  Plus,
  Zap,
  Trophy,
  Clock,
  FileText,
  Settings
} from 'lucide-react';
import PdfDownloadButton from '@/components/PdfDownloadButton';

// Week/Day colors based on the reference images
const weekColors = {
  6: { header: 'bg-gradient-to-r from-purple-600 to-purple-500', icon: 'text-purple-600', label: 'Initial Planning' },
  5: { header: 'bg-gradient-to-r from-cyan-500 to-blue-500', icon: 'text-cyan-600', label: 'Handstock Prep' },
  4: { header: 'bg-gradient-to-r from-teal-500 to-emerald-500', icon: 'text-teal-600', label: 'Roster & Equipment' },
  3: { header: 'bg-gradient-to-r from-orange-500 to-amber-500', icon: 'text-orange-600', label: 'Team Training' },
  2: { header: 'bg-gradient-to-r from-pink-500 to-rose-500', icon: 'text-pink-600', label: 'Final Prep' },
  1: { header: 'bg-gradient-to-r from-blue-600 to-blue-500', icon: 'text-blue-600', label: 'Ready Week' },
  'monday': { header: 'bg-gradient-to-r from-green-600 to-emerald-500', icon: 'text-green-600', label: 'Count Day 1' },
  'tuesday': { header: 'bg-gradient-to-r from-blue-500 to-sky-500', icon: 'text-blue-600', label: 'Count Day 2' },
};

// Complete checklist data from all reference images
const defaultWeeklyTasks = {
  6: {
    title: 'Week 6',
    subtitle: 'Initial Planning',
    items: [
      { id: 'w6-1', text: 'Assign the stocktake controller role to an experienced member of the leadership team and confirm your stocktake champion and auditor.', assignee: 'Complex / Coordinator' },
      { id: 'w6-2', text: 'Ensure you have the most recent version of the stocktake workbooks and latest policy and procedures.', assignee: 'Stocktake Controller' },
      { id: 'w6-3', text: 'Plan your rosters using the resourcing guidelines in the Operations Stocktake Timeline & Resource Overview, taking into consideration MVA and the four week roster cycle.', assignee: 'Stocktake Controller' },
      { id: 'w6-4', text: 'All Team Members to complete Stocktake Fundamentals online course on Workday.', assignee: 'Stocktake Controller' },
      { id: 'w6-5', text: 'Ensure all locators are correct and all decommissioned handstock bays are removed.', assignee: 'All Leadership Team' },
      { id: 'w6-6', text: 'Review each week to capture BLC changes, relays etc. (See stocktake guidelines in Chapter One - Preparation - Operations)', assignee: 'Stocktake Controller' },
      { id: 'w6-7', text: 'Ensure the pricing team are up to date with the price audit cycle and that all bays and locators used for stocktake have been included in the audit cycle.', assignee: 'Administration Supervisor' },
      { id: 'w6-8', text: 'Review all mobile devices to ensure they are working. Check whole store for complete Wi-Fi coverage. Log faulty devices with Technology Service Desk.', assignee: 'Administration Supervisor' },
      { id: 'w6-9', text: 'Contact your stocktake auditor & confirm their visit dates/times. Ensure the auditor has a copy of their checklist.', assignee: 'Complex / Coordinator' },
      { id: 'w6-10', text: 'Full store walk with Coordinators and Inventory Specialist to review stock preparation & housekeeping.', assignee: 'Complex / Coordinator' },
      { id: 'w6-11', text: 'Take action on outstanding CI Credits, Claims, ETOS, Isolations, Customer repair and returns, CRC and all Customer Orders.', assignee: 'Goods Inwards Supervisor' },
      { id: 'w6-12', text: 'Audit Topstock for correct items and quantity and make the relevant adjustments.', assignee: 'Operations Manager & Inventory Specialist' },
    ]
  },
  5: {
    title: 'Week 5',
    subtitle: 'Handstock Prep',
    items: [
      { id: 'w5-1', text: 'Commence handstock preparation: Item numbers on cartons, No cartons to have mixed stock, No counts on boxes or bays.', assignee: 'All Leadership Team' },
    ]
  },
  4: {
    title: 'Week 4',
    subtitle: 'Roster & Equipment',
    items: [
      { id: 'w4-1', text: 'Ensure the store has enough ladders, steps, WAVEs, Banners etc. Arrange additional equipment as needed.', assignee: 'Stocktake Controller' },
      { id: 'w4-2', text: 'Publish your rosters taking into consideration MVAs and the four week roster cycle.', assignee: 'Stocktake Controller' },
      { id: 'w4-3', text: 'Determine what Direct Deliveries are outstanding and follow up with customer and/or supplier.', assignee: 'In Home and Builders Coordinator' },
      { id: 'w4-4', text: 'Communicate action plan to all leaders and supervisors via stocktake briefings. Start countdown for team with key messages via daily meetings, Workplace and posters.', assignee: 'Stocktake Controller' },
      { id: 'w4-5', text: 'Ensure housekeeping standards are maintained. Return stock from offices, nursery huts, desks, GI, fallen between dexion etc back to the shelf.', assignee: 'All Leadership Team' },
      { id: 'w4-6', text: 'Review rosters: Confirm shifts with team, replace absenteeism, confirm with borrowed team members, review total head count.', assignee: 'All Leadership Team' },
    ]
  },
  3: {
    title: 'Week 3',
    subtitle: 'Team Training',
    items: [
      { id: 'w3-1', text: 'Ensure printer is working. If any problems log with Technology Service Desk.', assignee: 'Administration Supervisor' },
      { id: 'w3-2', text: 'Ensure all Leaders and Supervisors complete Stocktake Masterclass and Investigations Online course in Workday.', assignee: 'Stocktake Controller' },
    ]
  },
  2: {
    title: 'Week 2',
    subtitle: 'Final Prep',
    items: [
      { id: 'w2-1', text: 'Bag and/or bundle hard to count products as needed.', assignee: 'Stocktake Controller' },
    ]
  },
  1: {
    title: 'Week 1',
    subtitle: 'Ready Week',
    items: [
      { id: 'w1-1', text: "Stocktake Auditor visit. Refer to National stocktake auditor's checklist.", assignee: 'Stocktake Auditor' },
      { id: 'w1-2', text: "Preparation for Nursery and Landscape: Blocking on benches, Labelling bagged goods and pavers in high rise, Ensure clean and tidy standards, identify and label store use.", assignee: 'Lifestyles Coordinator' },
      { id: 'w1-3', text: "Preparation for Timber Yard: Review ticket standards, Add item numbers to timber packs, Identify and label store use, Ensure clean and tidy standards.", assignee: 'Builders Coordinator' },
      { id: 'w1-4', text: "Prepare Customer Orders: Review storage areas and relocate all cancelled orders to shop floor, Identify off range products and label with 'Do Not Count', Ensure clean and tidy standards.", assignee: 'In Home Coordinator' },
      { id: 'w1-5', text: "Confirm the DC delivery schedule for Stocktake week.", assignee: 'Operations Manager' },
      { id: 'w1-6', text: "Finalise returns to CRC and review draft batches. Action the CRC by locator report.", assignee: 'Operations Manager' },
    ]
  },
  'monday': {
    title: 'Monday',
    subtitle: 'Count Day 1',
    items: [
      { id: 'mon-1', text: 'Conduct audit of Topstock.', assignee: 'Operations Manager' },
      { id: 'mon-2', text: 'Review and set up stocktake access in back office for all team including borrowed team.', assignee: 'Stocktake Controller' },
      { id: 'mon-3', text: 'Check all additional mobile devices are charged and prepare Stocktake workstation area.', assignee: 'Stocktake Controller' },
      { id: 'mon-4', text: 'Confirm loan mobile devices have automatically assigned to your location. Check instructions if any issues, contact Technology Service Desk.', assignee: 'Administration Supervisor' },
      { id: 'mon-5', text: 'Process all outstanding CRC stock. Also action CRC by locator report.', assignee: 'Goods Inwards Supervisor' },
      { id: 'mon-6', text: "Identify and mark all products in GI requiring isolations: Customer repairs, Actioned claims, Product recalls and isolations, Off range, Fixtures and fittings, Processed CRC batches. Label with 'Do Not Count'.", assignee: 'Goods Inwards Supervisor' },
      { id: 'mon-7', text: 'Count timberyard, trade yard and indoor timber after 5pm.', assignee: 'Builders Coordinator' },
      { id: 'mon-8', text: 'Count Greenlife / landscaping yards after 5pm.', assignee: 'Lifestyles Coordinator' },
      { id: 'mon-9', text: 'Print and check missed count reports as bays are completed. Complete all missed count corrections by end of Monday count day. Do not enter counts of 0 where no stock has been located.', assignee: 'Stocktake Controller' },
      { id: 'mon-10', text: 'Count and key hand stock once store is closed.', assignee: 'All Leadership Team' },
      { id: 'mon-11', text: 'Clear communication to team on Monday morning via workplace post, poster in team area that handstock is counted.', assignee: 'Stocktake Controller' },
    ]
  },
  'tuesday': {
    title: 'Tuesday',
    subtitle: 'Count Day 2',
    items: [
      { id: 'tue-1', text: 'Notify IPC via email once Goods Inwards is isolated.', assignee: 'Operations Manager' },
      { id: 'tue-2', text: 'Ensure all Admin Cut Offs are actioned as per Chapter Two - Admin Cut Offs - Operations.', assignee: 'Complex / Coordinator' },
      { id: 'tue-3', text: 'Stock Adjustments to be approved by COB Tuesday (includes Unfulfilled COS, Team Requests, Vendor Request and Recounts). Remember to also review Procurement system.', assignee: 'Operations Manager' },
      { id: 'tue-4', text: 'Action the Intransit report. EGR any stock received into the store prior to GI isolation.', assignee: 'Goods Inwards Supervisor' },
      { id: 'tue-5', text: 'Send finalised IRNA report to IPC as specified in IPC e-mail.', assignee: 'Operations Manager / Goods Inwards Supervisor' },
      { id: 'tue-6', text: 'Internal Transfer Report - Review and ensure all donations are processed.', assignee: 'Administration Supervisor' },
      { id: 'tue-7', text: 'Ensure COS Job Board is actioned. Any completed Direct Deliveries / Installations must have supplier invoice processed by IPC.', assignee: 'In Home / Builders / Service Coordinator' },
      { id: 'tue-8', text: "Finalise CRC batches, submit into EGR and book collection. Segregate CRC pallets and mark 'CRC - DO NOT COUNT'.", assignee: 'Goods Inwards Supervisor' },
      { id: 'tue-9', text: 'Print and check missed count reports as bays are completed. Complete all missed count corrections by end of count day.', assignee: 'Stocktake Controller' },
    ]
  }
};

const Checklist = () => {
  const { user } = useContext(AuthContext);
  const [expandedWeeks, setExpandedWeeks] = useState({ 6: true });
  const [completedTasks, setCompletedTasks] = useState({});
  const [weeklyTasks, setWeeklyTasks] = useState(defaultWeeklyTasks);
  const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);
  const [selectedWeekForTask, setSelectedWeekForTask] = useState(null);
  const [newTaskText, setNewTaskText] = useState('');
  const [newTaskAssignee, setNewTaskAssignee] = useState('');
  const token = localStorage.getItem('token');

  // Get the current store_id (for Network Admin, check localStorage)
  const getStoreId = () => {
    if (user?.role === 'network_admin' || user?.role === 'regional_manager') {
      return localStorage.getItem('selectedStoreId') || user?.store_id;
    }
    return user?.store_id;
  };

  // Order for display: Weeks 6-1, then Monday, Tuesday
  const displayOrder = [6, 5, 4, 3, 2, 1, 'monday', 'tuesday'];

  useEffect(() => {
    loadProgress();
    
    // Listen for store changes
    const handleStoreChange = () => {
      loadProgress();
    };
    window.addEventListener('storeChanged', handleStoreChange);
    
    return () => {
      window.removeEventListener('storeChanged', handleStoreChange);
    };
  }, [user?.store_id]);

  const loadProgress = async () => {
    try {
      const storeId = getStoreId();
      const url = storeId ? `${API}/weekly-checklist-progress?store_id=${storeId}` : `${API}/weekly-checklist-progress`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompletedTasks(data.completed_tasks || {});
        if (data.custom_tasks) {
          const merged = { ...defaultWeeklyTasks };
          Object.entries(data.custom_tasks).forEach(([week, tasks]) => {
            if (merged[week]) {
              merged[week].items = [...merged[week].items, ...tasks];
            }
          });
          setWeeklyTasks(merged);
        }
      }
    } catch (error) {
      const saved = localStorage.getItem('weekly_checklist_progress');
      if (saved) {
        const data = JSON.parse(saved);
        setCompletedTasks(data.completed_tasks || {});
      }
    }
  };

  const saveProgress = async (newCompleted, customTasks = null) => {
    const storeId = getStoreId();
    const data = { 
      completed_tasks: newCompleted,
      store_id: storeId
    };
    if (customTasks) {
      data.custom_tasks = customTasks;
    }
    
    localStorage.setItem('weekly_checklist_progress', JSON.stringify(data));
    
    try {
      await fetch(`${API}/weekly-checklist-progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
    } catch (error) {
      console.error('Failed to save progress', error);
    }
  };

  const toggleTask = (taskId) => {
    const newCompleted = {
      ...completedTasks,
      [taskId]: completedTasks[taskId] ? null : {
        completed_at: new Date().toISOString(),
        completed_by: user?.name || 'Unknown'
      }
    };
    if (!newCompleted[taskId]) {
      delete newCompleted[taskId];
    }
    setCompletedTasks(newCompleted);
    saveProgress(newCompleted);
    toast.success(newCompleted[taskId] ? 'Task completed!' : 'Task unmarked');
  };

  const toggleWeek = (week) => {
    setExpandedWeeks(prev => ({
      ...prev,
      [week]: !prev[week]
    }));
  };

  const addCustomTask = () => {
    if (!newTaskText.trim() || !selectedWeekForTask) {
      toast.error('Please enter task details');
      return;
    }

    const newTask = {
      id: `custom-${Date.now()}`,
      text: newTaskText,
      assignee: newTaskAssignee || 'Custom Task',
      isCustom: true
    };

    const updatedTasks = { ...weeklyTasks };
    updatedTasks[selectedWeekForTask].items = [
      ...updatedTasks[selectedWeekForTask].items,
      newTask
    ];
    
    setWeeklyTasks(updatedTasks);
    setAddTaskModalOpen(false);
    setNewTaskText('');
    setNewTaskAssignee('');
    setSelectedWeekForTask(null);
    
    const customTasks = {};
    Object.entries(updatedTasks).forEach(([week, data]) => {
      const customs = data.items.filter(t => t.isCustom);
      if (customs.length > 0) {
        customTasks[week] = customs;
      }
    });
    saveProgress(completedTasks, customTasks);
    toast.success('Custom task added!');
  };

  // Calculate stats
  const allTasks = Object.values(weeklyTasks).flatMap(w => w.items);
  const totalTasks = allTasks.length;
  const completedCount = Object.keys(completedTasks).filter(id => completedTasks[id]).length;
  const remainingCount = totalTasks - completedCount;
  const progressPercentage = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;

  const getWeekProgress = (week) => {
    const items = weeklyTasks[week]?.items || [];
    const completed = items.filter(item => completedTasks[item.id]).length;
    const total = items.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  };

  return (
    <div className="space-y-6" data-testid="checklist-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl border border-teal-200 p-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target className="w-8 h-8 text-teal-600" />
            <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-wide">Stocktake Checklist</h1>
          </div>
          <PdfDownloadButton 
            contentId="checklist-content" 
            filename="Count_Week_Checklist" 
            title="Count Week Checklist"
          />
        </div>
        <p className="text-slate-600 mt-2">Track your preparation and weekly tasks</p>
      </div>

      <div id="checklist-content">
        {/* 6-Week Preparation Journey */}
        <Card className="bg-gradient-to-br from-slate-50 to-teal-50/30 border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900 uppercase">6-Week Preparation Journey</h2>
            <p className="text-sm text-slate-600">Track your weekly preparation tasks</p>
          </div>
          <Button 
            onClick={() => {
              setSelectedWeekForTask(6);
              setAddTaskModalOpen(true);
            }}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="add-custom-task-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Custom Task
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="bg-white p-4 border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{completedCount}</div>
                <div className="text-xs text-slate-500">Completed</div>
              </div>
            </div>
          </Card>
          <Card className="bg-white p-4 border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Circle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{remainingCount}</div>
                <div className="text-xs text-slate-500">Remaining</div>
              </div>
            </div>
          </Card>
          <Card className="bg-white p-4 border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Zap className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{totalTasks}</div>
                <div className="text-xs text-slate-500">Total Tasks</div>
              </div>
            </div>
          </Card>
          <Card className="bg-white p-4 border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Trophy className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{progressPercentage}%</div>
                <div className="text-xs text-slate-500">Progress</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Overall Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Overall Progress</span>
            <span className="text-sm text-slate-600">{completedCount} / {totalTasks}</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </Card>

      {/* Weekly Accordion */}
      <div className="space-y-4">
        {displayOrder.map((week) => {
          const weekData = weeklyTasks[week];
          if (!weekData) return null;
          
          const progress = getWeekProgress(week);
          const isExpanded = expandedWeeks[week];
          const colors = weekColors[week];

          return (
            <Card key={week} className="overflow-hidden border-0 shadow-sm" data-testid={`week-${week}-section`}>
              {/* Week Header */}
              <button
                onClick={() => toggleWeek(week)}
                className={`w-full ${colors.header} p-4 flex items-center justify-between text-white`}
                data-testid={`week-${week}-toggle`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                    {progress.percentage === 100 ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <Clock className="w-5 h-5" />
                    )}
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-lg">{weekData?.title}</div>
                    <div className="text-sm opacity-80">{weekData?.subtitle}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-2xl font-bold">{progress.percentage}%</div>
                    <div className="text-sm opacity-80">{progress.completed}/{progress.total} done</div>
                  </div>
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </button>

              {/* Week Progress Bar */}
              <div className="h-1 bg-white/20">
                <div
                  className="h-full bg-white/40 transition-all duration-500"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>

              {/* Week Content */}
              {isExpanded && (
                <div className="bg-white divide-y divide-slate-100">
                  {weekData?.items.map((task, idx) => {
                    const isCompleted = !!completedTasks[task.id];
                    const completionData = completedTasks[task.id];

                    return (
                      <div
                        key={task.id}
                        className="flex items-start gap-4 p-4 hover:bg-slate-50 transition-colors"
                        data-testid={`task-${task.id}`}
                      >
                        <button
                          onClick={() => toggleTask(task.id)}
                          className={`mt-1 flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                            isCompleted 
                              ? 'bg-green-500 border-green-500 text-white' 
                              : 'border-slate-300 hover:border-teal-500'
                          }`}
                          data-testid={`checkbox-${task.id}`}
                        >
                          {isCompleted && <CheckCircle2 className="w-5 h-5" />}
                        </button>

                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-relaxed ${isCompleted ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                            {task.text}
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            <Badge className="bg-slate-800 text-white hover:bg-slate-800 text-xs">
                              {task.assignee}
                            </Badge>
                            {isCompleted && completionData && (
                              <span className="text-xs text-green-600 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Done by {completionData.completed_by}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="text-sm text-slate-400 font-medium">
                          #{idx + 1}
                        </div>
                      </div>
                    );
                  })}

                  {/* Add Custom Task Button */}
                  <div className="p-4 text-center">
                    <button
                      onClick={() => {
                        setSelectedWeekForTask(week);
                        setAddTaskModalOpen(true);
                      }}
                      className="text-sm text-slate-500 hover:text-teal-600 flex items-center gap-2 mx-auto"
                      data-testid={`add-task-week-${week}`}
                    >
                      <Plus className="w-4 h-4" />
                      Add Custom Task to {weekData?.title}
                    </button>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Add Task Modal */}
      <Dialog open={addTaskModalOpen} onOpenChange={setAddTaskModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Task Description</label>
              <Input
                value={newTaskText}
                onChange={(e) => setNewTaskText(e.target.value)}
                placeholder="Enter task description..."
                className="mt-2"
                data-testid="new-task-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Assignee (optional)</label>
              <Input
                value={newTaskAssignee}
                onChange={(e) => setNewTaskAssignee(e.target.value)}
                placeholder="e.g., Stocktake Controller"
                className="mt-2"
                data-testid="new-task-assignee"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setAddTaskModalOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={addCustomTask} className="flex-1 bg-teal-600 hover:bg-teal-700">
              Add Task
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default Checklist;
